/**
 * PPGable — the ProximityParks icon shape, as an interface element.
 *
 * From the brandbook (p.27): "Since the icon is an asset that belongs to us
 * completely, we can use it in our own way, making it a unique distinctive
 * feature of the ProximityParks branding ... As a dynamic element that frames,
 * contains, highlights or organizes the items in a composition."
 *
 * The shape is the logo mark's gable: a peaked pentagon with angled shoulders.
 * The book draws it two ways and both are reproduced here —
 *
 *   outline   a hairline, used to frame or mark without adding weight
 *   wash      a large translucent panel, red-tinted or neutral, used to hold
 *             a headline (p.36's layout does exactly this)
 *
 * It is deliberately NOT decorative filler. Used everywhere it would stop
 * meaning anything; used on the one panel that carries the deal's headline
 * figure it does what the book asks of it.
 */

export default function PPGable({
  variant = "outline",
  tone = "neutral",
  className = "",
  strokeWidth = 1,
  ...rest
}) {
  // The gable: flat base, angled shoulders rising to a peak just off-centre,
  // matching the proportions of the mark rather than a generic pentagon.
  const d = "M2 62 L2 26 L32 4 L62 26 L62 62 Z";

  const stroke = tone === "red" ? "rgba(233,53,41,0.55)" : "rgba(244,244,244,0.22)";
  const fill = variant === "wash"
    ? (tone === "red" ? "rgba(233,53,41,0.07)" : "rgba(244,244,244,0.04)")
    : "none";

  return (
    <svg viewBox="0 0 64 64" aria-hidden="true" focusable="false"
         className={className} {...rest}>
      <path d={d} fill={fill} stroke={stroke} strokeWidth={strokeWidth}
            strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

/**
 * A headline panel framed by the gable, the way the book's layouts are built:
 * a large translucent shape behind the figure that matters most, bleeding off
 * the corner rather than sitting politely inside it.
 */
export function GableHeadline({ children, tone = "red", className = "" }) {
  return (
    <div className={`relative overflow-hidden ${className}`}>
      {/* The gable keeps its proportions. Stretching it flat - which the first
          version did with preserveAspectRatio="none" - turns the mark into an
          anonymous diagonal and the brand asset stops being recognisable,
          which is the one thing it exists to be. It bleeds off the top-right
          corner the way the book's layouts bleed it off a page edge. */}
      <PPGable
        variant="wash"
        tone={tone}
        preserveAspectRatio="xMidYMax meet"
        className="pointer-events-none absolute -right-16 -top-[42%] h-[230%] w-[46%] opacity-80"
      />
      <div className="relative">{children}</div>
    </div>
  );
}
